/**
 * Browser polling backoff based on user inactivity.
 *
 * - Active: use the caller's base interval
 * - ≥30 min idle: 10% speed (10× base interval)
 * - ≥2 h idle: once every 90 minutes
 * - Any user activity while backoffs apply: reset to normal immediately
 */

export const ACTIVITY_IDLE_SOFT_MS = 30 * 60 * 1000;
export const ACTIVITY_IDLE_HARD_MS = 2 * 60 * 60 * 1000;
export const ACTIVITY_HARD_POLL_MS = 90 * 60 * 1000;
/** Soft idle: poll at 10% of normal rate → multiply interval by 10 */
export const ACTIVITY_SOFT_INTERVAL_MULTIPLIER = 10;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'];

let lastActivityAt = Date.now();
let trackingInitialized = false;
/** @type {Set<(info: { prevTier: string, nextTier: string }) => void>} */
const tierListeners = new Set();

export function getLastActivityAt() {
  return lastActivityAt;
}

/**
 * @param {number} [now]
 * @returns {'active' | 'soft' | 'hard'}
 */
export function getIdleTier(now = Date.now()) {
  const idleMs = Math.max(0, now - lastActivityAt);
  if (idleMs >= ACTIVITY_IDLE_HARD_MS) return 'hard';
  if (idleMs >= ACTIVITY_IDLE_SOFT_MS) return 'soft';
  return 'active';
}

/**
 * @param {number} baseMs
 * @param {number} [now]
 * @returns {number}
 */
export function getEffectivePollInterval(baseMs, now = Date.now()) {
  const base = Math.max(0, Number(baseMs) || 0);
  const tier = getIdleTier(now);
  if (tier === 'hard') return ACTIVITY_HARD_POLL_MS;
  if (tier === 'soft') return Math.max(base * ACTIVITY_SOFT_INTERVAL_MULTIPLIER, base);
  return base;
}

/**
 * Record user activity. Notifies listeners when leaving a backoff tier.
 */
export function noteUserActivity() {
  const prevTier = getIdleTier();
  lastActivityAt = Date.now();
  if (prevTier !== 'active') {
    const nextTier = 'active';
    tierListeners.forEach((listener) => {
      try {
        listener({ prevTier, nextTier });
      } catch (_) {
        // ignore listener errors
      }
    });
  }
}

/**
 * Subscribe to inactivity-tier improvements (soft/hard → active).
 * @param {(info: { prevTier: string, nextTier: string }) => void} listener
 * @returns {() => void} unsubscribe
 */
export function onActivityTierReset(listener) {
  if (typeof listener !== 'function') return () => {};
  tierListeners.add(listener);
  return () => {
    tierListeners.delete(listener);
  };
}

function onActivityEvent() {
  noteUserActivity();
}

function onVisibilityChange() {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    noteUserActivity();
  }
}

/**
 * Start global activity listeners once (safe to call multiple times).
 */
export function initActivityTracking() {
  if (typeof window === 'undefined' || trackingInitialized) return;
  trackingInitialized = true;
  lastActivityAt = Date.now();

  ACTIVITY_EVENTS.forEach((eventName) => {
    // mousemove/scroll are noisy; use passive + coalesce via noteUserActivity (cheap)
    window.addEventListener(eventName, onActivityEvent, { passive: true, capture: true });
  });
  document.addEventListener('visibilitychange', onVisibilityChange);
}

/**
 * Schedule a recurring poll with inactivity backoff.
 * Uses recursive timeouts so each tick re-reads the effective interval.
 * When the user returns from soft/hard idle, the next wait is rescheduled immediately.
 *
 * @param {() => (void|Promise<void>)} callback
 * @param {number|(() => number)} baseIntervalMs
 * @param {{ immediate?: boolean, enabled?: boolean }} [options]
 * @returns {() => void} stop
 */
export function startActivityAwarePolling(callback, baseIntervalMs, options = {}) {
  const immediate = options.immediate !== false;
  let stopped = false;
  let timer = null;
  let inFlight = false;

  const resolveBase = () => {
    const raw = typeof baseIntervalMs === 'function' ? baseIntervalMs() : baseIntervalMs;
    return Math.max(0, Number(raw) || 0);
  };

  const clearTimer = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    clearTimer();
    if (stopped) return;
    const delay = getEffectivePollInterval(resolveBase());
    timer = setTimeout(runTick, delay);
  };

  const runTick = async () => {
    if (stopped) return;
    if (inFlight) {
      schedule();
      return;
    }
    inFlight = true;
    try {
      await callback();
    } catch (_) {
      // Callers should handle their own errors; keep polling.
    } finally {
      inFlight = false;
      if (!stopped) schedule();
    }
  };

  const unsubscribe = onActivityTierReset(() => {
    if (stopped) return;
    // Drop long backoff wait, poll soon, then resume at the active-tier interval.
    clearTimer();
    runTick();
  });

  initActivityTracking();

  if (immediate) {
    runTick();
  } else {
    schedule();
  }

  return () => {
    stopped = true;
    clearTimer();
    unsubscribe();
  };
}
